import { apiFetch } from './api';
import { invalidateCachedResource, readCachedResource } from './requestCache';
import type {
  Invoice,
  PaymentMethod,
  PriceInfo,
  SubscriptionSummary,
} from '../store/features/billing/types';

export type BillingFetchOptions = {
  force?: boolean;
};

export type BillingCacheScope =
  | 'all'
  | 'invoices'
  | 'paymentMethods'
  | 'prices'
  | 'subscription';

const BILLING_CACHE_TTL_MS = 60_000;
const BILLING_PRICES_CACHE_KEY = 'billing:prices';
const BILLING_PAYMENT_METHODS_CACHE_KEY = 'billing:payment-methods';
const BILLING_INVOICES_CACHE_KEY = 'billing:invoices';
const BILLING_SUBSCRIPTION_CACHE_KEY = 'billing:subscription';

export function invalidateBillingCache(scope: BillingCacheScope = 'all'): void {
  if (scope === 'all' || scope === 'prices') {
    invalidateCachedResource(BILLING_PRICES_CACHE_KEY);
  }
  if (scope === 'all' || scope === 'paymentMethods') {
    invalidateCachedResource(BILLING_PAYMENT_METHODS_CACHE_KEY);
  }
  if (scope === 'all' || scope === 'invoices') {
    invalidateCachedResource(BILLING_INVOICES_CACHE_KEY);
  }
  if (scope === 'all' || scope === 'subscription') {
    invalidateCachedResource(BILLING_SUBSCRIPTION_CACHE_KEY);
  }
}

export async function fetchBillingPrices(
  options: BillingFetchOptions = {},
): Promise<PriceInfo[]> {
  return readCachedResource(
    BILLING_PRICES_CACHE_KEY,
    async () => {
      const response = await apiFetch<{ prices: PriceInfo[] }>('/api/billing/prices');
      return Array.isArray(response?.prices) ? response.prices : [];
    },
    {
      force: options.force,
      ttlMs: BILLING_CACHE_TTL_MS,
    },
  );
}

export async function fetchBillingPaymentMethods(
  options: BillingFetchOptions = {},
): Promise<PaymentMethod[]> {
  return readCachedResource(
    BILLING_PAYMENT_METHODS_CACHE_KEY,
    async () => {
      const response = await apiFetch<{ paymentMethods: PaymentMethod[] }>(
        '/api/billing/payment-methods',
      );
      return Array.isArray(response?.paymentMethods) ? response.paymentMethods : [];
    },
    {
      force: options.force,
      ttlMs: BILLING_CACHE_TTL_MS,
    },
  );
}

export async function fetchBillingInvoices(
  options: BillingFetchOptions = {},
): Promise<Invoice[]> {
  return readCachedResource(
    BILLING_INVOICES_CACHE_KEY,
    async () => {
      const response = await apiFetch<{ invoices: Invoice[] }>('/api/billing/invoices');
      return Array.isArray(response?.invoices) ? response.invoices : [];
    },
    {
      force: options.force,
      ttlMs: BILLING_CACHE_TTL_MS,
    },
  );
}

export async function fetchBillingSubscription(
  options: BillingFetchOptions = {},
): Promise<SubscriptionSummary | null> {
  return readCachedResource(
    BILLING_SUBSCRIPTION_CACHE_KEY,
    async () => {
      const response = await apiFetch<{ subscription: SubscriptionSummary | null }>(
        '/api/billing/subscription',
      );
      return response?.subscription ?? null;
    },
    {
      force: options.force,
      ttlMs: BILLING_CACHE_TTL_MS,
    },
  );
}
